import { MagnifyingGlass, X } from "@phosphor-icons/react"
import {
  FieldError as AriaFieldError,
  Input as AriaInput,
  type InputProps as AriaInputProps,
  Label as AriaLabel,
  SearchField as AriaSearchField,
  type SearchFieldProps as AriaSearchFieldProps,
  Text as AriaText,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { IconButton } from "../button/IconButton"
import { Icon } from "../icon/Icon"
import { Tooltip } from "../overlays/tooltip/Tooltip"
import { LabelAndDescriptionStack } from "./Field"
import { Slot } from "./TextField"

const iconSizeLookup = {
  small: "w-3 h-3",
  medium: "w-[14px] h-[14px]",
  large: "w-5 h-5",
}

/**
 * A field for filtering or searching.
 *
 * ```tsx
 * import { SearchField } from "@tokenterminal/core/ui/input/SearchField"
 *
 * <SearchField />
 * ```
 */

export type SearchFieldProps = AriaSearchFieldProps & {
  description?: string
  errorMessage?: string
  label?: string
  placeholder?: string
  size?: "small" | "medium" | "large"
  inputProps?: AriaInputProps & { className?: string }
  trailingSlot?: React.ReactNode
}
export const SearchField = ({
  "aria-label": ariaLabel,
  description,
  errorMessage,
  label,
  placeholder = "Search",
  size = "medium",
  inputProps: _inputProps,
  trailingSlot,
  ...props
}: SearchFieldProps) => {
  const { className: inputClassName, ...inputProps } = _inputProps || {}

  return (
    <AriaSearchField
      className="tt-field-wrapper"
      // Only use a default aria-label if there's no label
      aria-label={ariaLabel ?? (label ? undefined : "Search")}
      {...props}
    >
      {(label || description) && (
        <LabelAndDescriptionStack>
          {label && <AriaLabel className="tt-field-label">{label}</AriaLabel>}
          {description && (
            <AriaText slot="description" className="tt-field-description">
              {description}
            </AriaText>
          )}
        </LabelAndDescriptionStack>
      )}
      <div className="tt-input-container" data-size={size}>
        <Slot>
          <Icon
            as={MagnifyingGlass}
            className={twMerge("text-fg-secondary", iconSizeLookup[size])}
          />
        </Slot>
        <AriaInput
          className={twMerge("tt-input-base", inputClassName)}
          placeholder={placeholder}
          {...inputProps}
        />
        {trailingSlot ? (
          <Slot>{trailingSlot}</Slot>
        ) : (
          <Slot className="tt-search-clear-button-slot">
            <Tooltip content="Clear">
              <IconButton
                variant="ghost"
                size="xsmall"
                label="Clear"
                icon={X}
                className="shrink-0"
                onPress={() => {
                  if (props.onChange) {
                    props.onChange("")
                  }
                }}
              />
            </Tooltip>
          </Slot>
        )}
      </div>
      <AriaFieldError className="tt-field-error">{errorMessage}</AriaFieldError>
    </AriaSearchField>
  )
}
